import {useGridApiRef, GridAutosizeOptions} from "@mui/x-data-grid-premium";
import {useEffect, useMemo, useRef} from "react";

export type AutoSizeGridParams<T> = {
    data: T[],
    loading: boolean,
    outliersFactor?: number
    includeOutliers?: boolean
    expand?: boolean
    columns: string[]
};

export function useAutoSizeGrid<T>(params: AutoSizeGridParams<T>) {

    const apiRef = useGridApiRef();
    const previousDataLengthRef = useRef<number>(0);

    const autoSizeOptions: GridAutosizeOptions = useMemo(() => ({
        columns: params.columns,
        includeOutliers: params.includeOutliers ?? false,
        includeHeaders: true,
        expand: params.expand ?? true,
        outliersFactor: params.outliersFactor ?? 2.0
    }), [params.columns, params.includeOutliers, params.expand, params.outliersFactor]);

    useEffect(() => {
        const dataLength = params.data?.length ?? 0;
        const wasLoading = previousDataLengthRef.current === -1;
        const dataLengthChanged = dataLength !== previousDataLengthRef.current;
        
        // Only autosize when:
        // 1. Not currently loading
        // 2. We have data
        // 3. Either we just finished loading (wasLoading && !params.loading) or data length changed significantly
        const shouldAutosize = !params.loading && 
                               dataLength > 0 && 
                               (wasLoading || (dataLengthChanged && dataLength > 0));
        
        if (shouldAutosize && apiRef.current) {
            // Use requestAnimationFrame to avoid blocking the main thread
            requestAnimationFrame(() => {
                if (apiRef.current) {
                    void apiRef.current.autosizeColumns(autoSizeOptions);
                }
            });
            previousDataLengthRef.current = dataLength;
        } else if (params.loading) {
            // Track that we're loading
            previousDataLengthRef.current = -1;
        }
    }, [params.loading, params.data?.length, autoSizeOptions, apiRef]);

    return apiRef;
}

